import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import round from 'lodash/round';
import clsx from 'clsx';
import { theme } from 'theme';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';

import { Loader, Triangle, Title } from 'ui';
import { EmptyDataLabel } from 'ui/Labels';
import { ESGScore } from 'modules/ESG';
import { useReportPage } from 'shared/hooks';

const BorderLinearProgress = withStyles({
  root: {
    height: 13,
    borderRadius: 20,
    backgroundColor: '#AECCE5',
    marginBottom: useReportPage ? 5 : 20,
  },
  bar: {
    borderRadius: 20,
    backgroundColor: '#013B6C',
  },
})(LinearProgress);

const BorderDownstreamProgress = withStyles({
  root: {
    height: 13,
    borderRadius: 20,
    backgroundColor: 'transparent',
  },
  bar: {
    borderRadius: 20,
    backgroundColor: '#598CB8',
  },
})(LinearProgress);

// eslint-disable-next-line @typescript-eslint/no-shadow
const useStyles = makeStyles((theme) => ({
  divider: {
    alignSelf: 'stretch',
    height: 'auto',
    marginTop: 120,
  },
  treeParent: {
    position: 'relative',
    paddingTop: 50,
  },
  treeParentPDF: {
    width: '80rem',
    transform: 'scale(0.57)',
    marginLeft: '-18rem',
    paddingTop: 0,
  },
  mobileTreeParent: {
    paddingTop: 20,
  },
  treeChild: {
    float: 'left',
    textAlign: 'center',
    position: 'relative',
    padding: `${useReportPage ? '50px' : '120px'} 5px 0 5px`,

    '&:before, &:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: '50%',
      borderTop: `2px dashed ${theme.palette.primary.main}`,
      width: '50%',
      height: useReportPage ? 65 : 120,
    },

    '&:after': {
      right: 'auto',
      left: useReportPage ? 'calc(50% + 1px)' : 'calc(50% + 2px)',
      borderLeft: `2px dashed ${theme.palette.primary.main}`,
    },

    '&:only-child::before, &:only-child::after': {
      display: 'none',
    },

    '&:only-child': {
      paddingTop: 0,
    },

    '&:first-child::before': {
      borderRight: `2px dashed ${theme.palette.primary.main}`,
    },
  },
  treeLeft: {
    '&::before': {
      border: 0,
    },

    '&::after': {
      borderLeft: 0,
    },
  },
  treeRight: {
    '&::after': {
      borderTop: 0,
    },
  },
  treeConnector: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: useReportPage ? -12 : 8,
      left: '50%',
      borderLeft: `2px dashed ${theme.palette.primary.main}`,
      width: 0,
      height: useReportPage ? 33 : 40,
    },
  },
  weightBox: {
    position: 'absolute',
    left: '50%',
    top: useReportPage ? -5 : 25,
    transform: `translate(-50%, 0) ${useReportPage ? 'scale(0.7)' : ''}`,
    display: 'flex',
    justifyContent: 'center',
    width: 180,
    margin: '15px auto 0',
    zIndex: 1,
  },
  weight: {
    fontSize: 18,
    marginLeft: 10,
  },
  itemWrapper: {
    maxWidth: '33.2%',
  },
  scoreBox: {
    backgroundColor: theme.palette.primary.main,
    padding: '10px 25px',
    color: theme.palette.primary.contrastText,
    fontSize: 18,
    borderRadius: 10,
    marginBottom: 15,
    marginRight: 10,
  },
  score: {
    fontSize: 40,
    fontWeight: 500,
  },
  scoreTotal: {
    fontSize: 18,
    fontWeight: 500,
  },
  scoreText: {
    position: 'relative',
    width: 185,
    fontSize: 21,
  },
  scoreTextLeft: {
    display: 'flex',
    width: 200,
  },
  scoreTextLeftTotal: {
    marginLeft: useReportPage ? '3.5%' : '14.2%',
    marginTop: useReportPage ? 20 : 36,
  },
  scoreTextBankTotal: {
    marginLeft: useReportPage ? '3.5%' : 0,
    marginTop: useReportPage ? 20 : 36,
  },
  scoreWrapper: {
    position: 'relative',
    padding: '0 50px',
    transform: useReportPage ? 'scale(0.7)' : 'none',
    marginTop: useReportPage ? -65 : 0,
    marginBottom: 4,

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 94,
      left: '50%',
      borderLeft: `2px dashed ${theme.palette.primary.main}`,
      width: 0,
      height: 121,
    },
  },
  scoreWrapperTotal: {
    position: 'relative',
    padding: useReportPage ? '0 180px' : '0 50px',
    transform: useReportPage ? 'scale(0.7)' : 'none',
    marginTop: useReportPage ? -22 : 0,
    marginBottom: 12,
  },
  scoreWrapperTotalBank: {
    padding: useReportPage ? '0' : '0 50px',
  },
  triangleBottom: {
    position: 'absolute',
    left: '37%',
    bottom: -65,
  },
  triangleMiddle: {
    position: 'absolute',
    left: 'calc(50% - 11px)',
    bottom: -21,
    transform: 'translate(-50%, 0)',
    zIndex: 1,
  },
  progressWrapper: {
    width: useReportPage ? 'auto' : 300,
    margin: '0 auto',
  },
  mobileFontSize: {
    fontSize: 10,
  },
  totalExtCosts: {
    position: 'absolute',
    marginTop: 97,
  },
  weightBoxReport: {
    padding: 8,
    width: 210,
  },
  triangleMiddleTotal: {
    top: useReportPage ? 72 : 48,
    transform: useReportPage ? 'scale(1.3) !important' : 'scale(1)',
  },
  textField: {
    width: 210,
    margin: theme.spacing(1, 0),
    background: theme.palette.custom.lightGray,
    textAlign: 'center',
    borderRadius: theme.spacing(1),
  },
  textFieldValue: {
    fontWeight: 'bold',
    padding: theme.spacing(0.5, 0),
    fontSize: useReportPage ? 12 : 16,
  },
  totalExtCostsTitle: {
    fontWeight: 600,
    fontSize: useReportPage ? 34 : 24,
  },
  descriptionBox: {
    fontWeight: 800,
    padding: theme.spacing(2),
    minWidth: 325,
    transform: 'translateY(-30%)',
    textAlign: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  scoreTextBank: {
    position: 'relative',
    width: 185,
    fontSize: 18,
  },
  containerBank: {
    width: '31rem',
  },
  withMargin: {
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    paddingRight: '5rem',
  },
  externalCostWrapper: {
    position: 'relative',
    padding: '0 50px',
    transform: useReportPage ? 'scale(0.7)' : 'none',
    marginTop: useReportPage ? -65 : 0,
    marginBottom: 4,

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 100,
      left: '50%',
      borderLeft: `2px dashed ${theme.palette.primary.main}`,
      width: 0,
      height: 123,
    },
  },
  triangleBottomScore: {
    marginBottom: '30px!important',
  },
  weightBoxTop: {
    marginTop: 0,
  },
  flip: {
    transform: 'rotate(360deg) scaleX(-1)',
    bottom: useReportPage ? 18 : 33,
  },
}));

const ESGScoreOverview = ({ scores, loading }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const totalScore = scores[3];

  const impactReviewOther = () => (
    <Grid item className={clsx([classes.scoreText, classes.scoreTextLeft])}>
      <Box>
        <span>{t('ESG impact review')}</span>
        <Title
          inline
          tooltip={t(
            'The underlying of each score is the monetised value a company’s external impacts as a percentage of its revenues.The score reflects how that value compares to the world benchmark, derived from a macroeconomic model (OECD input-output tables) that considers 450 sectors in 44 countries.',
          )}
        />
      </Box>
      <Triangle variant="right" size="small" />
    </Grid>
  );

  const externalCostOther = () => (
    <Grid
      item
      className={clsx(classes.scoreText, classes.scoreTextLeft, classes.scoreTextLeftTotal)}
    >
      {t('External cost / 1 EUR revenue')} <Triangle variant="right" size="small" />
    </Grid>
  );

  return (
    <Paper>
      <Box p={2}>
        <Typography variant="h6">{t('Score overview')}</Typography>
        {loading ? (
          <Loader center />
        ) : (
          <div>
            {scores.length === 0 ? (
              <Box py={2}>
                <EmptyDataLabel label={t('There are no data available yet.')} />
              </Box>
            ) : (
              <>
                <Box py={2}>
                  <Divider />
                </Box>
                <Box
                  className={clsx(classes.treeParent, { [classes.treeParentPDF]: useReportPage })}
                >
                  <Grid
                    container
                    justifyContent="space-evenly"
                    alignItems="center"
                    className={classes.scoreWrapper}
                  >
                    {impactReviewOther()}
                    <Grid item className={classes.scoreBox} p={10}>
                      <Typography component="span" className={classes.scoreTotal}>
                        <Typography className={classes.score} component="span">
                          {totalScore.score}
                        </Typography>
                        /10
                      </Typography>
                      <Triangle
                        variant="top"
                        size="small"
                        className={clsx(classes.triangleMiddle)}
                      />
                    </Grid>
                    <Grid item className={classes.scoreText} />
                  </Grid>
                  <Grid container alignItems="center" className={clsx(classes.scoreWrapperTotal)}>
                    {externalCostOther()}
                    <Grid item>
                      <Box
                        className={clsx(classes.weightBox, {
                          [classes.weightBoxReport]: useReportPage,
                        })}
                      >
                        <div className={classes.textField}>
                          <Typography
                            component="h6"
                            variant="h6"
                            className={clsx(classes.textFieldValue, classes.totalExtCostsTitle)}
                          >
                            {round(totalScore.extCostsToRevenue, 2)} EUR
                          </Typography>
                        </div>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    className={clsx(classes.treeParent, classes.treeConnector, {
                      [classes.mobileTreeParent]: useReportPage,
                    })}
                  >
                    {scores
                      .filter(({ title }) => title !== 'Total')
                      .map(({ title, score, extCostsToRevenue }, index) => {
                        const roundedExtCostsToRevenue = round(extCostsToRevenue, 2);
                        return (
                          <Fragment key={title}>
                            <Grid
                              item
                              xs
                              className={clsx(classes.itemWrapper, classes.treeChild, {
                                [classes.treeLeft]: index === 0,
                                [classes.treeRight]: index === scores.length - 2,
                              })}
                            >
                              <Box className={classes.weightBox}>
                                <div className={classes.textField}>
                                  <Typography
                                    component="h6"
                                    variant="h6"
                                    className={classes.textFieldValue}
                                  >
                                    {roundedExtCostsToRevenue} EUR
                                  </Typography>
                                </div>
                              </Box>
                              <Box p={2}>
                                <ESGScore title={title} score={score} />
                              </Box>
                            </Grid>
                            {index < scores.length - 2 && (
                              <Divider className={classes.divider} orientation="vertical" />
                            )}
                          </Fragment>
                        );
                      })}
                  </Grid>
                </Box>
              </>
            )}
          </div>
        )}
      </Box>
    </Paper>
  );
};

ESGScoreOverview.propTypes = {
  loading: PropTypes.bool.isRequired,
  scores: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      score: PropTypes.number.isRequired,
      extCostsToRevenue: PropTypes.number.isRequired,
    }),
  ).isRequired,
};

export default ESGScoreOverview;
